import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useTheme,
  Grid
} from "@mui/material"
import ClearIcon from "@mui/icons-material/Clear"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import React, { useEffect, useState } from "react"
import { Col, Row } from "react-bootstrap"
import StyledLabel from "components/StyledComponents/StyledLabel"
import { FieldArray } from "formik"
import AppTextField from "components/StyledComponents/AppTextField"
import { AddOutlined } from "@mui/icons-material"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import AppButton from "components/StyledComponents/AppButton"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import ImageSearchField from "components/FileUpload/ImageSearchField"
import styles from "../../../menu.module.scss"
import { marketingActions } from "redux/marketing/slice/user"
import { useDispatch, useSelector } from "react-redux"
import {
  generateDidYouKnowAction,
  uploadDidYouKnowImageAction,
  enhanceDidYouKnowFieldAction
} from "redux/marketing/actions/user"
import ImageCropper from "components/ImageCropper"
import CheckBox from "components/CheckBox"

const DID_YOU_KNOW_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/svg+xml",
  "image/gif",
  "image/webp"
]
// GIF/WebP are uploaded as-is (skip the cropper) so animation/format is preserved
const SKIP_CROP_TYPES = ["image/gif", "image/webp"]

const DidYouKnowForm = ({ formik, values, name, buttonText, label, isEdit, saveBeforeAi }) => {
  const runWithSave = (run) => (saveBeforeAi ? saveBeforeAi(formik, run) : run())
  const theme = useTheme()
  const dispatch = useDispatch()
  const [isExpanded, setIsExpanded] = useState(false)
  const [openCrop, setCrop] = useState(false)
  const [fileDetails, setFileDetails] = useState(null)
  const [imageObjectURL, setImageObjectUrl] = useState(null)
  const [imgIndex, setImgIndex] = useState()
  const [aiLoadingKey, setAiLoadingKey] = useState(null)
  const { didYouKnowData, menu } = useSelector((state) => state.marketing)

  const handleFieldEnhance = (index, subField, currentValue) => {
    if (!menu?.menu_item_ID) return
    runWithSave(() => {
      const loadingKey = `${index}-${subField}`
      setAiLoadingKey(loadingKey)
      dispatch(
        enhanceDidYouKnowFieldAction({
          payload: {
            item_id: menu?.menu_item_ID,
            field: subField === "heading" ? "did_you_know_heading" : "did_you_know_content",
            current_value: currentValue || ""
          },
          onSuccess: (val) => {
            formik.setFieldValue(`${name}[${index}].${subField}`, val)
            setAiLoadingKey(null)
          },
          onError: () => setAiLoadingKey(null)
        })
      )
    })
  }

  const renderAiButton = (index, subField, currentValue) => {
    if (!isEdit || !menu?.menu_item_ID) return null
    const isLoading = aiLoadingKey === `${index}-${subField}`
    const hasContent = !!(currentValue && currentValue.trim())
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        {/* Invisible label-height spacer so the button lines up with the input box
            (each field renders a body1Regular label above its input). */}
        <StyledLabel variant="body1Regular" aria-hidden style={{ visibility: "hidden" }}>
          &nbsp;
        </StyledLabel>
        <AppButton
          styles={{
            height: "50px",
            padding: "0 16px",
            fontSize: "13px",
            whiteSpace: "nowrap",
            minWidth: "132px",
            justifyContent: "center"
          }}
          disabled={isLoading}
          onClick={() => handleFieldEnhance(index, subField, currentValue)}>
          <AutoAwesomeIcon sx={{ fontSize: 16, mr: 0.7 }} />
          {isLoading
            ? hasContent
              ? "Refining..."
              : "Generating..."
            : hasContent
            ? "Refine"
            : "Generate"}
        </AppButton>
      </div>
    )
  }

  const handleGenerateDidYouKnow = () => {
    runWithSave(() => {
      const payload = {
        menu_ids: [menu?.menu_item_ID]
      }
      dispatch(generateDidYouKnowAction(payload))
    })
  }

  useEffect(() => {
    if (didYouKnowData?.did_you_know_items?.length) {
      const existingData = formik.values?.[name]
      const hasExistingData =
        existingData?.length > 0 &&
        existingData.some((item) => item?.heading || item?.content || item?.image)

      const items = didYouKnowData?.did_you_know_items[0] || []
      const mappedData = items?.map((item) => ({
        heading: item?.title || "",
        content: item?.description || "",
        image: item?.image || "",
        suggested_images: item?.images || []
      }))

      if (hasExistingData) {
        formik.setFieldValue(name, [...existingData, ...mappedData])
      } else {
        formik.setFieldValue(name, mappedData)
      }
    }
  }, [didYouKnowData])

  const onImageUpload = (image, index) => {
    setImgIndex(index)
    if (SKIP_CROP_TYPES.includes(image?.type)) {
      uploadImageFile(image, index, formik)
      return
    }
    setCrop(true)
    setFileDetails(image)
    setImageObjectUrl(URL.createObjectURL(image))
  }

  function uploadImageFile(image, index, formik) {
    let data = new FormData()
    data.append("media", image)
    data.append("media_type", "IMAGE")
    data.append("content_type", image.type)
    dispatch(uploadDidYouKnowImageAction({ index, data }))
    image.preview = URL.createObjectURL(image)
    formik.setFieldValue(`${name}[${index}].image`, image)
    formik.setFieldTouched(`${name}[${index}].image`)
  }

  function handleCroppedImage(image, formik) {
    uploadImageFile(image, imgIndex, formik)
  }

  function showCropperModal(imageObjectURL, fileDetails, formik) {
    return (
      <ImageCropper
        setSelectedImage={(image) => handleCroppedImage(image, formik)}
        srcImage={imageObjectURL}
        fileDetail={fileDetails}
        aspectRatio={1}
        onClose={setCrop}
        openCrop={openCrop}></ImageCropper>
    )
  }

  const handleDeleteImage = (formik, index) => {
    dispatch(marketingActions.setDidYouKnowImage({ index, id: null }))
    formik.setFieldValue(`${name}[${index}].image`, null)
  }

  const handleSearchImageConfirm = (formik, index, media) => {
    dispatch(marketingActions.setDidYouKnowImage({ index, id: media?.id }))
    formik.setFieldValue(`${name}[${index}].image`, { id: media?.id, url: media?.url })
    formik.setFieldTouched(`${name}[${index}].image`)
  }
  return (
    <div>
      {showCropperModal(imageObjectURL, fileDetails, formik)}
      <Box
        flexDirection="column"
        className={styles.accordian_container}
        sx={{
          backgroundColor: isExpanded
            ? theme.palette.background.main
            : theme.palette.background.default,
          paddingBottom: !isExpanded && "0px !important",
          margin: isExpanded && "36px 0px !important"
        }}>
        <Accordion expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
          <AccordionSummary className={styles.summary}>
            <Box display="flex" alignItems="center" gap={1}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              <Typography variant="bodyLyon">Did You know</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {isEdit && (
              <Row className="w-100 m-0 mt-2">
                <Col lg="12" className="d-flex justify-content-end">
                  <AppButton sx={{ padding: "10px 16px" }} onClick={handleGenerateDidYouKnow}>
                    Generate Did You Know
                  </AppButton>
                </Col>
              </Row>
            )}
            <Row className="w-100 m-0">
              <StyledLabel>{label}</StyledLabel>
              <FieldArray
                name={name}
                render={(arrayHelper) => (
                  <>
                    {values?.length
                      ? values?.map((did_you_know, index) => (
                          <>
                            <Col lg="12" className="align-items-center mt-4">
                              <Typography
                                variant="body1Regular"
                                sx={{
                                  color: theme.palette.secondary.main,
                                  textUnderlineOffset: "2px"
                                }}>
                                {`Card #${index + 1}`}
                              </Typography>
                            </Col>
                            <Row
                              className="mt-2"
                              style={{ paddingRight: 0 }}
                              id={`${name}-${index}`}
                              key={`${name}-${index}`}>
                              <Col lg="11">
                                <Col lg="12">
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: 8,
                                      width: "100%"
                                    }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <AppTextField
                                        id={`${name}[${index}].heading`}
                                        name={`${name}[${index}].heading`}
                                        label="Heading"
                                        isUpperCase={true}
                                        type="text"
                                        fullWidth
                                        placeholder={`Enter Heading`}
                                        value={did_you_know?.heading || ""}
                                        onChange={(event) => {
                                          formik.setFieldValue(
                                            `${name}[${index}].heading`,
                                            event.target.value
                                          )
                                        }}
                                        onBlur={formik.handleBlur(`${name}[${index}].heading`)}
                                        error={Boolean(formik.errors?.[name]?.[index]?.heading)}
                                        helperText={formik.errors?.[name]?.[index]?.heading}
                                      />
                                    </div>
                                    {renderAiButton(index, "heading", did_you_know?.heading)}
                                  </div>
                                </Col>
                                <Col lg="12" className="mt-4">
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: 8,
                                      width: "100%"
                                    }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <AppTextField
                                        id={`${name}[${index}].content`}
                                        name={`${name}[${index}].content`}
                                        label="Content"
                                        isUpperCase={true}
                                        type="text"
                                        fullWidth
                                        placeholder="Enter Content"
                                        value={did_you_know?.content || ""}
                                        onChange={(event) => {
                                          formik.setFieldValue(
                                            `${name}[${index}].content`,
                                            event.target.value
                                          )
                                        }}
                                      />
                                    </div>
                                    {renderAiButton(index, "content", did_you_know?.content)}
                                  </div>
                                </Col>
                                <Col lg="12" className="mt-4">
                                  <ImageUploadField
                                    label={"Image"}
                                    isVariant={true}
                                    placeholder={`Upload Image`}
                                    allowedTypes={DID_YOU_KNOW_IMAGE_TYPES}
                                    value={did_you_know?.image || ""}
                                    onChange={(e) => onImageUpload(e, index)}
                                    onDelete={() => handleDeleteImage(formik, index)}
                                  />
                                </Col>
                                <Col lg="12" className="mt-4">
                                  <ImageSearchField
                                    onConfirm={(media) =>
                                      handleSearchImageConfirm(formik, index, media)
                                    }
                                  />
                                </Col>
                                {did_you_know?.suggested_images?.length > 0 && (
                                  <Col lg="12" className="mt-4">
                                    <StyledLabel>Suggested Images</StyledLabel>
                                    <Grid container spacing={2}>
                                      {did_you_know.suggested_images.map((img, imgIdx) => {
                                        const currentImage = did_you_know?.image
                                        const currentImageUrl =
                                          currentImage instanceof File
                                            ? currentImage.preview
                                            : currentImage?.url || currentImage || ""
                                        const isChecked = currentImageUrl === img?.url

                                        return (
                                          <Grid item xs={4} key={imgIdx}>
                                            <Box
                                              sx={{
                                                position: "relative",
                                                cursor: "pointer",
                                                border: isChecked
                                                  ? `2px solid ${theme.palette.secondary.main}`
                                                  : `2px solid ${theme.palette.background.main}`,
                                                borderRadius: "12px",
                                                padding: "4px",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor: theme.palette.background.main
                                              }}
                                              onClick={() => {
                                                formik.setFieldValue(`${name}[${index}].image`, img)
                                              }}>
                                              <img
                                                src={img?.url}
                                                alt={`suggested-${imgIdx}`}
                                                style={{
                                                  width: "100%",
                                                  borderRadius: "8px",
                                                  aspectRatio: "1",
                                                  objectFit: "cover"
                                                }}
                                              />
                                              <Box
                                                sx={{
                                                  position: "absolute",
                                                  top: 8,
                                                  left: 8,
                                                  zIndex: 2,
                                                  backgroundColor: "rgba(0,0,0,0.5)",
                                                  borderRadius: "4px",
                                                  display: "flex",
                                                  padding: "2px"
                                                }}>
                                                <CheckBox checked={isChecked} width={18} />
                                              </Box>
                                            </Box>
                                          </Grid>
                                        )
                                      })}
                                    </Grid>
                                  </Col>
                                )}
                              </Col>
                              <Col
                                lg="1"
                                className="mt-4"
                                style={{ display: "flex", justifyContent: "center" }}>
                                <span role="button" onClick={() => arrayHelper.remove(index)}>
                                  <ClearIcon sx={{ width: "30px", height: "48px" }} />
                                </span>
                              </Col>
                            </Row>
                          </>
                        ))
                      : null}
                    <Row className="m-0 mt-5">
                      <AppButton
                        fullWidth
                        onClick={() =>
                          arrayHelper.push({
                            heading: "",
                            content: "",
                            image: "",
                            suggested_images: []
                          })
                        }
                        startIcon={<AddOutlined />}>
                        {buttonText}
                      </AppButton>
                    </Row>
                  </>
                )}
              />
            </Row>
          </AccordionDetails>
        </Accordion>
      </Box>
    </div>
  )
}

export default DidYouKnowForm
